import { useEffect } from 'react';
import { createContext, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket from '../../hooks/useWebSocket2';
import {
  addMessage,
  incrementUnReadMessage,
  removeMessage,
  selectChat,
  setDialogue,
  updateSession,
} from '../../redux/liveChat/slice';
import { selectCurrentChat } from '../../redux/liveChat/selectors';

import { updateMessage } from '../../redux/liveChat/slice';
import toast from 'react-hot-toast';
import LetterNotification from '../customComponents/LetterNotification/LetterNotification';
import { fetchUser } from '../../api/usersApi';
import {
  addStream,
  removeStream,
  updateStream,
} from '../../redux/videoChat/slice';
import ChatNotification from '../customComponents/ChatNotification/ChatNotification';
import { addOnlineUser, removeOnlineUser } from '../../redux/websocket/slice';
import { updateUserBalance } from '../../redux/auth/slice';
import { fetchAgencyUserById } from '../../api/agencyUsers';
import { selectIsManager, selectUser } from '../../redux/auth/selectors';
import { useSounds } from '../../hooks/useSounds';
import SignUpNotification from '../customComponents/SignUpNotification/SignUpNotification';

const WebSocketContext = createContext(null);
export const useWebSocketContext = () => useContext(WebSocketContext);

const props = {
  typingId: null,
};

const WebSocketProvider = ({ children }) => {
  const isManager = useSelector(selectIsManager);
  const currentUser = useSelector(selectUser);
  const dispatch = useDispatch();
  const currentChat = useSelector(selectCurrentChat);
  const { socket, sendData, addCallback, isConnected } = useWebSocket();
  const { playChatNotification, playReplyChatNotification } = useSounds();

  useEffect(() => {
    const handleMessage = async message => {
      if (message.chatId === currentChat?._id) {
        if (message.senderId !== currentUser._id) {
          playReplyChatNotification();
        }
        
        dispatch(addMessage(message));
      } else {
        let user;
        const isSupport =
          message.senderType === 'agencyUserSupport' ||
          message.senderType === 'support';
        if (isSupport) {
          user = await fetchAgencyUserById(message.senderId);
        } else {
          user = await fetchUser(message.senderId);
        }
        const condition1 = !isManager;
        const condition2 = message.senderType !== 'paidUserSupport';
        const condition3 = message.senderId !== currentUser._id;

        if (condition1 & condition2 && condition3) {
          playChatNotification();
          toast(
            t => <ChatNotification letter={message} id={t.id} user={user} />,
            {
              duration: 30000, //TODO change duration of notification
              position: 'top-right',
            },
          );
        }
        dispatch(incrementUnReadMessage(message.chatId));
      }

      dispatch(updateSession({ chatId: message.chatId, lastMessage: message }));
      dispatch(setDialogue({ _id: message.chatId, lastMessage: message }));
    };
    addCallback('sendMessage', handleMessage);
  }, [currentChat, addCallback, dispatch, isManager, currentUser]);

  useEffect(() => {
    const handleMessage = async message => {
      const user = await fetchUser(message.senderId);
      playChatNotification();
      toast(
        t => <LetterNotification letter={message} id={t.id} user={user} />,
        {
          duration: 30000, //TODO change duration of notification
          position: 'top-right',
        },
      );
    };
    addCallback('newLetter', handleMessage);
  }, [addCallback, dispatch]);

  useEffect(() => {
    const handleLoginUser = async data => {
      playChatNotification();
      toast(t => <SignUpNotification user={data} id={t.id} />, {
        duration: 30000, //TODO change duration of notification
        position: 'top-right',
      });
    };
    addCallback('loginUser', handleLoginUser);
  }, [addCallback, dispatch]);

  useEffect(() => {
    const handleUpdateBalance = async data => {
      const { balance } = data;
      dispatch(updateUserBalance(balance));
    };
    addCallback('updateBalance', handleUpdateBalance);
  }, [addCallback, dispatch]);

  useEffect(() => {
    const handleUpdateMessage = message => {
      dispatch(updateMessage(message));
    };
    addCallback('updateMessage', handleUpdateMessage);
  }, [addCallback, dispatch]);

  useEffect(() => {
    const handleRemoveMessage = ({ messageId }) => {
      dispatch(removeMessage(messageId));
    };
    addCallback('removeMessage', handleRemoveMessage);
  }, [addCallback, dispatch]);

  useEffect(() => {
    const handleUserTyping = ({ chatId }) => {
      if (!currentChat?._id || chatId !== currentChat?._id) {
        return;
      }
      dispatch(selectChat({ ...currentChat, userTyping: true }));
      if (props.typingId) {
        clearTimeout(props.typingId);
      }
      props.typingId = setTimeout(() => {
        dispatch(selectChat({ ...currentChat, userTyping: false }));
        props.typingId = null;
      }, 3000);
    };
    addCallback('userTyping', handleUserTyping);
  }, [addCallback, dispatch, currentChat?._id]);

  useEffect(() => {
    const handleAddStream = stream => {
      dispatch(addStream(stream));
    };
    const handleUpdateStream = data => {
      dispatch(updateStream(data));
    };
    const handleRemoveStream = ({ meetingId }) => {
      dispatch(removeStream(meetingId));
    };
    const handleAddOnlineUser = ({ id }) => {
      dispatch(addOnlineUser(id));
    };
    const handleRemoveOnlineUser = ({ id }) => {
      dispatch(removeOnlineUser(id));
    };
    const handleChangeBotStatus = ({ status }) => {
      if (status === 'ENABLED') {
        toast.success('Live Chat Bot is sending messages to all Online Users ');
      } else {
        toast.error('Live Chat Bot has been stopped');
      }
    };

    addCallback('createStream', handleAddStream);
    addCallback('updateStream', handleUpdateStream);
    addCallback('removeStream', handleRemoveStream);
    addCallback('addOnlineUser', handleAddOnlineUser);
    addCallback('removeOnlineUser', handleRemoveOnlineUser);
    addCallback('changeBotStatus', handleChangeBotStatus);
  }, [addCallback, dispatch]);

  return (
    <WebSocketContext.Provider
      value={{ sendData, addCallback, socket, isConnected }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
